import { matchesHistoricalArchive } from './historicalArchiveCompatibility';
import { stableStringify } from '../checklist/ChecklistSnapshotCreationService';
import { historicalTables, loadHistoricalSnapshotRows, type HistoricalRow } from './HistoricalSnapshotHydrationService';
import type { LocalTombstoneRow } from './TombstoneSyncService';

export const retiredHistoryTables = {
  templates: 'guincho_templates_checklist', fields: 'guincho_campos_template',
  parents: historicalTables.parents, values: 'guincho_valores_checklist',
  images: 'guincho_marcacao_imagens',
};
export interface HistoricalCheckpoint {
  signature: string;
  root: number;
  parent: number;
  phase: 'fields' | 'values' | 'images';
  offset: number;
  proofParentId?: string;
  completedAt?: number;
  retry: boolean;
}

class HistoricalRoundLimit extends Error {}
type Entry = [string, HistoricalRow];
export interface RetiredHistoryDependencies {
  tombstones(): Promise<LocalTombstoneRow[]>;
  snapshotsEnabled: boolean;
  fetch(table: string, filters: Record<string, string | null>, offset: number, limit: number): Promise<HistoricalRow[]>;
  read(table: string, id: string): Promise<HistoricalRow | null>;
  refreshProtection(reuse?: boolean): Promise<void>;
  allowed(table: string, row: HistoricalRow): Promise<boolean>;
  insertAbsent(table: string, row: HistoricalRow): Promise<number>;
  materializeImages(rows: HistoricalRow[], revalidate: () => Promise<boolean>): Promise<boolean | void>;
  materializePhoto?(row: HistoricalRow, revalidate: () => Promise<boolean>): Promise<boolean>;
  loadCheckpoint?(): HistoricalCheckpoint | null;
  saveCheckpoint?(checkpoint: HistoricalCheckpoint): void;
  now?(): number;
  maxUnits?: number;
  maxRequests?: number;
}
const t = retiredHistoryTables;
const archiveTables = new Set([historicalTables.schemas, historicalTables.snapshots, historicalTables.fields]);
const identityKeys = ['empresa_id', 'template_id', 'checklist_id', 'snapshot_id', 'campo_id', 'snapshot_field_id', 'usuario_id'];
const compatible = (table: string, local: HistoricalRow, remote: HistoricalRow): boolean => {
  if (archiveTables.has(table)) return matchesHistoricalArchive(table, local, remote);
  return identityKeys.every(key => {
    // O vínculo nulo antigo será reparado pelo hidratador com CAS; nunca substituímos o pai.
    if (table === t.parents && key === 'snapshot_id' && local[key] == null) return true;
    return (local[key] ?? null) === (remote[key] ?? null);
  }) && !(remote.retired_at != null && local.retired_at == null);
};

/** Releitura limitada às cadeias atingidas por aposentadoria, independente do cursor incremental. */
export class RetiredChecklistHistoryService {
  private requests = 0;
  private protectionLoaded = false;
  private needsRetry = false;
  private writes = 0;
  private inserted = 0;
  constructor(private readonly deps: RetiredHistoryDependencies) {}

  private async fetch(table: string, filters: Record<string, string | null>, offset: number, limit: number): Promise<HistoricalRow[]> {
    if (++this.requests > (this.deps.maxRequests ?? 32)) throw new HistoricalRoundLimit();
    return this.deps.fetch(table, filters, offset, limit);
  }

  private async one(table: string, id: string, companyId: string): Promise<HistoricalRow | null> {
    const rows = await this.fetch(table, { id, empresa_id: companyId }, 0, 2);
    return rows.length === 1 && rows[0].id === id && rows[0].empresa_id === companyId ? rows[0] : null;
  }

  private async valid(entries: Entry[], companyId: string, requirePresent = false): Promise<boolean> {
    await this.deps.refreshProtection(this.protectionLoaded);
    this.protectionLoaded = true;
    for (const [table, row] of entries) {
      if (!row.id || row.empresa_id !== companyId || !await this.deps.allowed(table, row)) return false;
      const existing = await this.deps.read(table, row.id);
      if ((requirePresent && !existing) || (existing && !compatible(table, existing, row))) return false;
    }
    return true;
  }

  private async materialize(entries: Entry[], companyId: string): Promise<number> {
    let missing = false;
    for (const [table, row] of entries) {
      const local = await this.deps.read(table, row.id);
      if (local && !compatible(table, local, row)) { this.needsRetry = true; return 0; }
      if (!local) missing = true;
    }
    if (!missing) return 0;
    if (!await this.valid(entries, companyId)) { this.needsRetry = true; return 0; }
    let inserted = 0;
    for (const [table, row] of entries) {
      if (await this.deps.read(table, row.id)) continue;
      // Revalida a cadeia inteira após cada await: fila, sessão, delete e identidade podem mudar.
      if (!await this.valid(entries, companyId)) { this.needsRetry = true; return inserted; }
      if (++this.writes > (this.deps.maxUnits ?? 100)) throw new HistoricalRoundLimit();
      const count = await this.deps.insertAbsent(table, row);
      inserted += count;
      this.inserted += count;
      if (!await this.deps.read(table, row.id)) { this.needsRetry = true; return inserted; }
    }
    return inserted;
  }

  async run(companyId: string): Promise<number> {
    this.requests = 0;
    this.writes = 0;
    this.inserted = 0;
    this.protectionLoaded = false;
    this.needsRetry = false;
    const tombstones = (await this.deps.tombstones()).filter(row => row.empresa_id === companyId);
    const latest = new Map<string, LocalTombstoneRow>();
    for (const row of tombstones) {
      const key = `${row.entity_type}:${row.entity_id}`;
      const previous = latest.get(key);
      if (!previous || row.sequence > previous.sequence
        || (row.sequence === previous.sequence && row.id > previous.id)) latest.set(key, row);
    }
    const byTemplate = new Map<string, LocalTombstoneRow>();
    for (const row of latest.values()) {
      if (row.deletion_kind !== 'retire' || (row.entity_type !== t.templates && row.entity_type !== t.fields)) continue;
      const key = row.entity_type === t.templates ? row.entity_id : row.parent_id ?? row.entity_id;
      if (!byTemplate.has(key) || row.entity_type === t.templates) byTemplate.set(key, row);
    }
    const roots = [...byTemplate.values()].sort((a, b) => a.entity_id.localeCompare(b.entity_id));
    const signature = stableStringify(['active-parents-v2', this.deps.snapshotsEnabled, tombstones.map(row => [row.id, row.sequence, row.deletion_kind]).sort()]);
    const now = this.deps.now?.() ?? Date.now();
    const previous = this.deps.loadCheckpoint?.();
    let state: HistoricalCheckpoint = previous?.signature === signature ? { ...previous }
      : { signature, root: 0, parent: 0, phase: 'fields', offset: 0, retry: false };
    if (state.completedAt != null) {
      // Falhas também aguardam o ciclo normal; histórico concluído não reinicia a cada foco.
      if (now - state.completedAt < (state.retry ? 180_000 : 900_000)) return 0;
      state = { signature, root: 0, parent: 0, phase: 'fields', offset: 0, retry: false };
    }
    let units = 0;
    const save = () => { state.retry ||= this.needsRetry; this.deps.saveCheckpoint?.({ ...state }); };
    const nextRoot = () => {
      state = { signature, root: state.root + 1, parent: 0, phase: 'fields', offset: 0,
        retry: state.retry || this.needsRetry };
      save();
    };
    try {
      while (state.root < roots.length && units < (this.deps.maxUnits ?? 100)) {
        const root = roots[state.root];
        const target = await this.one(root.entity_type, root.entity_id, companyId);
        if (!target || target.retired_at == null || (root.parent_id && root.entity_type === t.fields && root.parent_id !== target.template_id)) { nextRoot(); continue; }
        const template = root.entity_type === t.templates ? target : await this.one(t.templates, target.template_id, companyId);
        if (!template || !await this.valid([[t.templates, template], [root.entity_type, target]], companyId)) { this.needsRetry = true; nextRoot(); continue; }
        const fields = new Map<string, HistoricalRow>();
        const fieldFor = async (id: string): Promise<HistoricalRow | null> => {
          if (fields.has(id)) return fields.get(id)!;
          const field = await this.one(t.fields, id, companyId);
          if (!field || field.template_id !== template.id) return null;
          fields.set(id, field);
          return field;
        };
        while (units < (this.deps.maxUnits ?? 100)) {
          const parents = state.phase === 'images' && state.proofParentId
            ? [await this.one(t.parents, state.proofParentId, companyId)]
            : await this.fetch(t.parents, { empresa_id: companyId, template_id: template.id, retired_at: null }, state.parent, 1);
          const parent = parents[0];
          if (!parent) {
            if (state.phase !== 'images' && state.proofParentId) { state.phase = 'images'; state.offset = 0; save(); continue; }
            nextRoot(); break;
          }
          if (parent.empresa_id !== companyId || parent.template_id !== template.id || parent.retired_at != null) {
            if (state.phase === 'images') { nextRoot(); break; }
            state.parent++; state.offset = 0; save(); continue;
          }
          const chain: Entry[] = [[t.templates, template]];
          let archive: Entry[] = [];
          if (parent.snapshot_id) {
            if (!this.deps.snapshotsEnabled) { this.needsRetry = true; if (state.phase === 'images') { nextRoot(); break; } state.parent++; state.offset = 0; save(); continue; }
            const loaded = await loadHistoricalSnapshotRows({ fetch: this.fetch.bind(this) }, parent);
            if (!loaded) { this.needsRetry = true; if (state.phase === 'images') { nextRoot(); break; } state.parent++; state.offset = 0; save(); continue; }
            archive = loaded;
            chain.push(...archive.filter(([table]) => table === historicalTables.schemas));
          }
          chain.push([t.parents, parent], ...archive.filter(([table]) => table !== historicalTables.schemas));
          if (!await this.valid(chain, companyId)) {
            this.needsRetry = true;
            if (state.phase === 'images') { nextRoot(); break; }
            state.parent++; state.offset = 0; save(); continue;
          }
          await this.materialize(chain, companyId);
          state.proofParentId = parent.id;
          const table = state.phase === 'fields' ? t.fields : state.phase === 'values' ? t.values : t.images;
          const filters: Record<string, string | null> = state.phase === 'values'
            ? { empresa_id: companyId, checklist_id: parent.id }
            : { empresa_id: companyId, template_id: template.id };
          const page = await this.fetch(table, filters, state.offset, 40);
          if (!page.length) {
            state.offset = 0;
            if (state.phase === 'fields') { state.phase = 'values'; }
            else if (state.phase === 'values') { state.parent++; }
            else { nextRoot(); break; }
            save(); continue;
          }
          for (const row of page) {
            if (units >= (this.deps.maxUnits ?? 100)) break;
            const rowChain = [...chain];
            let legitimate = row.empresa_id === companyId;
            let fieldType: string | undefined;
            if (state.phase === 'fields') {
              legitimate &&= row.template_id === template.id;
              fields.set(row.id, row);
            } else if (state.phase === 'values') {
              legitimate &&= row.checklist_id === parent.id;
              if (row.snapshot_field_id) {
                const field = archive.find(([name, field]) => name === historicalTables.fields && field.id === row.snapshot_field_id)?.[1];
                fieldType = field?.tipo;
                legitimate &&= Boolean(field && (!row.campo_id || row.campo_id === field.original_campo_id || row.campo_id === field.id));
                if (field && row.campo_id && row.campo_id !== field.id) {
                  const original = await fieldFor(row.campo_id);
                  if (original) rowChain.push([t.fields, original]); else legitimate = false;
                }
              } else {
                const field = await fieldFor(row.campo_id);
                fieldType = archive.find(([name, archived]) => name === historicalTables.fields && archived.original_campo_id === row.campo_id)?.[1].tipo ?? field?.tipo;
                if (field) rowChain.push([t.fields, field]); else legitimate = false;
              }
            } else {
              const field = await fieldFor(row.campo_id);
              legitimate &&= row.template_id === template.id && Boolean(field);
              if (field) rowChain.push([t.fields, field]);
            }
            rowChain.push([table, row]);
            if (legitimate) {
              await this.materialize(rowChain, companyId);
              if (state.phase === 'values' && fieldType === 'foto' && typeof row.valor === 'string'
                && /^https?:\/\//.test(row.valor) && this.deps.materializePhoto) {
                const cached = await this.deps.materializePhoto(row, async () => {
                  this.protectionLoaded = false;
                  if (!await this.valid(rowChain, companyId, true)) return false;
                  const current = await this.deps.read(t.values, row.id);
                  return current?.valor === row.valor;
                });
                if (!cached) this.needsRetry = true;
              }
              if (state.phase === 'images') {
                const cached = await this.deps.materializeImages([row], async () => {
                  this.protectionLoaded = false;
                  return this.valid(rowChain, companyId, true);
                });
                if (cached === false) this.needsRetry = true;
              }
            }
            state.offset++; units++; save();
          }
        }
      }
      if (state.root >= roots.length) { state.completedAt = this.deps.now?.() ?? Date.now(); save(); }
    } catch (error) {
      save();
      if (!(error instanceof HistoricalRoundLimit)) throw error;
    }
    return this.inserted;
  }
}
